/**
 * Snapshot of the last published content — keeps the live site stable while CMS edits.
 * Written on Publish; used for revert and for serving Live · Draft / Live · Ready docs.
 */

/** Editorial fields restored on revert / served while status is Live · Draft|Ready. */
export const PUBLISHED_SNAPSHOT_FIELDS: Record<string, readonly string[]> = {
  destinations: [
    "name",
    "name_localized",
    "content",
    "keywords",
    "images",
    "translation_meta",
    "research_brief",
    "research_draft",
    "research_challenge",
  ],
  guides: ["title", "description", "slug", "images", "destination", "author"],
  articles: ["title", "content", "slug", "images", "author", "destination"],
  authors: ["name", "bio", "slug", "avatar", "role"],
  site_chrome: [
    "name",
    "document_title_suffix",
    "footer_tagline",
    "main_nav",
    "drawer_nav",
    "footer_nav",
    "footer_company",
  ],
  page_copy: [
    "page_key",
    "title",
    "heading",
    "body",
    "cta_label",
    "cta_href",
    "featured_heading",
    "featured_body",
    "community_heading",
    "community_body",
    "sustainability_message",
    "how_it_works_heading",
    "how_it_works_step1_title",
    "how_it_works_step1_body",
    "how_it_works_step2_title",
    "how_it_works_step2_body",
    "how_it_works_step3_title",
    "how_it_works_step3_body",
    "popular_heading",
    "popular_aria",
    "chip_boat",
    "chip_hiking",
    "chip_lake",
    "chip_village",
    "trigger_label",
    "modal_title",
    "new_chat",
    "close_search",
    "suggestions_aria",
    "suggestions_heading",
    "empty_state",
    "describe_journey",
    "describe_placeholder",
    "send_message",
    "discover_aria",
    "consulting",
    "search_not_configured",
    "type_destination",
    "type_guide",
    "type_story",
    "plan_stay",
    "search_destinations_aria",
    "where_label",
    "where_placeholder",
    "chip_beach_label",
    "chip_beach_suggestion",
    "chip_alps_label",
    "chip_alps_suggestion",
    "chip_culture_label",
    "chip_culture_suggestion",
    "chip_sustain_label",
    "chip_sustain_suggestion",
    "chip_beach_mask_suggestion",
    "booking_title",
    "booking_home_lead",
    "booking_full_lead",
    "booking_submit",
    "departure",
    "destination_label",
    "where_from",
    "where_to",
    "check_in",
    "check_out",
    "adults",
    "children",
    "child_ages",
    "preferences",
    "preferences_placeholder",
    "transit_aria",
    "train",
    "flight",
    "ferry",
    "edit_search",
    "translation_meta",
  ],
  community_stories: [
    "quote",
    "author_name",
    "tier_level",
    "tier_label",
    "image_url",
    "alt_text",
    "sort_order",
  ],
  hotels: [
    "name",
    "content",
    "soft_tags",
    "enabled",
    "main_photo_url",
    "image_urls",
    "facilities",
    "facility_names",
    "star_rating",
    "guest_rating",
    "review_count",
    "address",
    "latitude",
    "longitude",
    "slug",
    "liteapi_hotel_id",
    "destination_id",
    "checkin_checkout",
    "hotel_type",
  ],
  home_hero_sets: [
    "title",
    "notes",
    "keywords_used",
    "slides",
  ],
};

const IGNORE_COMPARE_KEYS = new Set([
  "status",
  "updated_at",
  "created_at",
  "published_snapshot",
  "published_at",
]);

export function buildPublishedSnapshot(
  collectionId: string,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const fields = PUBLISHED_SNAPSHOT_FIELDS[collectionId];
  const snap: Record<string, unknown> = {
    captured_at: new Date().toISOString(),
  };
  if (fields) {
    for (const key of fields) {
      if (key in values) snap[key] = structuredCloneSafe(values[key]);
    }
    return snap;
  }
  // Fallback: clone everything except system keys
  for (const [key, value] of Object.entries(values)) {
    if (IGNORE_COMPARE_KEYS.has(key)) continue;
    snap[key] = structuredCloneSafe(value);
  }
  return snap;
}

export function applyPublishedSnapshot(
  values: Record<string, unknown>,
  snapshot: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...values };
  for (const [key, value] of Object.entries(snapshot)) {
    if (key === "captured_at") continue;
    next[key] = structuredCloneSafe(value);
  }
  return next;
}

export function hasPublishedSnapshot(values: Record<string, unknown> | undefined): boolean {
  const snap = values?.published_snapshot;
  return Boolean(snap && typeof snap === "object" && !Array.isArray(snap));
}

/** True when working values differ from previous (ignoring status timestamps). */
export function contentMeaningfullyChanged(
  previous: Record<string, unknown> | undefined,
  next: Record<string, unknown>,
): boolean {
  if (!previous) return true;
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  for (const key of keys) {
    if (IGNORE_COMPARE_KEYS.has(key)) continue;
    if (stableStringify(previous[key]) !== stableStringify(next[key])) return true;
  }
  return false;
}

function structuredCloneSafe<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value ?? null)) as T;
  }
}

function stableStringify(value: unknown): string {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return String(value);
  }
}

/** Overlay snapshot onto a doc for public/live API responses. */
export function resolveLiveDocument<T extends Record<string, unknown>>(
  doc: T,
): T {
  const status = typeof doc.status === "string" ? doc.status : "";
  if (status === "published" || status === "unpublishing") return doc;
  if (status !== "published_draft" && status !== "published_ready") return doc;
  const snap = doc.published_snapshot;
  if (!snap || typeof snap !== "object" || Array.isArray(snap)) return doc;
  const overlay: Record<string, unknown> = { ...doc };
  for (const [key, value] of Object.entries(snap as Record<string, unknown>)) {
    if (key === "captured_at") continue;
    overlay[key] = value;
  }
  // Keep workflow status / id / snapshot metadata from the working doc.
  overlay.status = doc.status;
  if ("_id" in doc) overlay._id = doc._id;
  overlay.published_snapshot = snap;
  return overlay as T;
}
